import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import {
  Loader2,
  Lock,
  Sparkles,
  MapPin,
  Star as StarIcon,
  Coins,
  Send,
  Crown,
  TrendingUp,
} from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import MatchRevealModal from "@/components/MatchRevealModal";
import NumericRating from "@/components/NumericRating";
import { VoiceIntroPlayer } from "@/components/VoiceIntroRecorder";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";

/**
 * Top Rated — featured spotlights of the 10 highest-rated members.
 * Identity is visible to everyone. Contacting requires reveal ($10 / credit / membership).
 */
export default function TopRated() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [revealMatch, setRevealMatch] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, tr] = await Promise.all([
        api.get("/top-rated"),
        api.get("/trending").catch(() => ({ data: { trending: [] } })),
      ]);
      const topItems = data.top_rated || [];
      setItems(topItems);
      // Deduplicate trending — hide anyone already featured in the Top Rated grid
      const shownIds = new Set(topItems.map((x) => x.user.id));
      const uniqueTrending = (tr.data?.trending || []).filter((t) => !shownIds.has(t.user.id));
      setTrending(uniqueTrending);
    } catch (err) {
      toast.error(errorMessage(err, "Could not load top rated"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Re-open reveal modal after a successful Stripe payment (?reveal=<user_id>)
  useEffect(() => {
    const revealUid = searchParams.get("reveal");
    if (!revealUid || loading) return;
    const item = items.find((x) => x.user.id === revealUid && x.unlocked);
    if (item) {
      setRevealMatch({ match_id: item.match_id, user: item.user });
      const next = new URLSearchParams(searchParams);
      next.delete("reveal");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, items, loading, setSearchParams]);

  const credits = user?.reveal_credits || 0;
  const subscribed = user?.subscription_active;

  const contactWithCredit = async (targetId) => {
    setBusyId(targetId);
    try {
      const { data: res } = await api.post(`/profiles/${targetId}/spend-credit`);
      await refresh();
      const { data: fresh } = await api.get("/top-rated");
      setItems(fresh.top_rated || []);
      const now = (fresh.top_rated || []).find((x) => x.user.id === targetId);
      if (now?.unlocked) {
        setRevealMatch({ match_id: res.match_id || now.match_id, user: now.user });
      }
    } catch (err) {
      toast.error(errorMessage(err, "Could not unlock"));
    } finally {
      setBusyId(null);
    }
  };

  const contactPaid = async (targetId) => {
    setBusyId(targetId);
    try {
      const { data: res } = await api.post(`/profiles/${targetId}/reveal-checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(errorMessage(err, "Could not start checkout"));
      setBusyId(null);
    }
  };

  const ratePhotoInline = async (userId, photoId, rating) => {
    try {
      const { data: r } = await api.post("/rate", { photo_id: photoId, rating });
      trackEvent("rate_photo", { score: rating, is_high: rating >= 8, source: "top-rated" });
      // Merge fresh photo stats back into the visible item so my_rating + avg update instantly.
      setItems((prev) => prev.map((it) => {
        if (it.user.id !== userId) return it;
        const fresh = (r.owner?.photos || []).find((p) => p.id === photoId);
        if (!fresh) return it;
        const nextPhotos = (it.user.photos || []).map((p) => p.id === photoId ? { ...p, ...fresh } : p);
        return { ...it, user: { ...it.user, photos: nextPhotos, overall_rating: r.owner?.overall_rating ?? it.user.overall_rating } };
      }));
      if (r.just_golden) toast(`\u2728 You pushed this photo to Golden!`, { duration: 4200 });
      if (r.streak?.credit_awarded) toast.success(`\uD83D\uDD25 ${r.streak.streak}-day streak! +1 credit`, { duration: 4200 });
      else toast.success(`Rated ${rating}/10`);
      if (refresh) refresh();
    } catch (err) {
      toast.error(errorMessage(err, "Could not save rating"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#722f37]" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-[#c39d2b]" />
            <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Top rated</span>
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl mt-2">
            The community's ten most admired.
          </h1>
          <p className="mt-3 text-lg text-[#5c5c5c]">
            Ranked by member ratings on the 1–10 scale. Free to browse — contact costs one reveal.
          </p>
        </div>
        {subscribed ? (
          <div className="glass px-5 py-3 rounded-full flex items-center gap-2 text-sm text-[#722f37] font-medium" data-testid="top-rated-subscribed">
            <Sparkles className="w-4 h-4 text-[#c39d2b]" /> Membership active — contact anyone
          </div>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="glass px-5 py-3 rounded-full flex items-center gap-2 text-sm text-[#722f37] font-medium" data-testid="top-rated-credits">
              <Coins className="w-4 h-4 text-[#c39d2b]" /> {credits} credit{credits === 1 ? "" : "s"}
            </div>
            <Link to="/pricing" className="btn-outline !py-2 !px-4 text-sm" data-testid="top-rated-get-credits">Get credits</Link>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="p-16 rounded-2xl bg-white border border-[#e5e0d8] card-elev text-center" data-testid="top-rated-empty">
          <Crown className="w-10 h-10 text-[#c39d2b] mx-auto" />
          <h3 className="font-serif-display text-3xl mt-4 text-[#722f37]">The leaderboard is still forming.</h3>
          <p className="mt-3 text-[#5c5c5c] text-lg">Rate photos on Discover to help shape it.</p>
          <div className="mt-6"><Link to="/discover" className="btn-primary" data-testid="top-rated-go-discover">Rate photos</Link></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="top-rated-grid">
          {items.map((it) => (
            <SpotlightCard
              key={it.user.id}
              item={it}
              subscribed={subscribed}
              credits={credits}
              currentUserId={user?.id}
              onContactCredit={() => contactWithCredit(it.user.id)}
              onContactPaid={() => contactPaid(it.user.id)}
              onRatePhoto={(photoId, score) => ratePhotoInline(it.user.id, photoId, score)}
              busy={busyId === it.user.id}
            />
          ))}
        </div>
      )}

      {revealMatch && (
        <MatchRevealModal match={revealMatch} onClose={() => setRevealMatch(null)} />
      )}

      {trending.length > 0 && (
        <section className="mt-20" data-testid="trending-section">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-5 h-5 text-[#722f37]" />
            <h2 className="font-serif-display text-3xl md:text-4xl">Trending now</h2>
            <span className="text-sm text-[#5c5c5c] ml-2">Profiles gaining the most rating momentum this week</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            {trending.map((t) => (
              <div key={t.user.id} className="rounded-2xl overflow-hidden bg-white border border-[#e5e0d8] card-elev" data-testid={`trending-card-${t.user.id}`}>
                <div className="relative aspect-[4/5] bg-[#eae5d9]">
                  <img src={t.user.photo} alt={t.user.display_name} className="w-full h-full object-cover" />
                  <div className="absolute top-3 left-3 rounded-full bg-[#722f37] text-white px-3 py-1 text-xs font-bold flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> +{t.delta.toFixed(1)}
                  </div>
                </div>
                <div className="p-3">
                  <div className="font-serif-display text-lg text-[#722f37] truncate">
                    {t.user.display_name}, {t.user.age}
                  </div>
                  <div className="text-xs text-[#5c5c5c] mt-1">
                    <span className="font-semibold text-[#c39d2b]">{t.week_avg}/10</span> this week · {t.week_ratings} ratings
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SpotlightCard({ item, subscribed, credits, currentUserId, onContactCredit, onContactPaid, onRatePhoto, busy }) {
  const u = item.user;
  const photo = u.photos?.[0];
  const rank = item.rank;
  const unlocked = item.unlocked;
  const [rating, setRating] = React.useState(null);
  const isSelf = currentUserId && u.id === currentUserId;

  // Rank 1 gets a slightly richer gold treatment
  const isChampion = rank === 1;

  const handleRate = async (score) => {
    setRating(score);
    try { await onRatePhoto(photo.id, score); }
    finally { setRating(null); }
  };

  return (
    <div
      className={`relative rounded-[1.75rem] overflow-hidden bg-white border card-elev flex flex-col ${
        isChampion ? "border-[#c39d2b] shadow-[0_20px_60px_rgba(196,157,43,0.22)]" : "border-[#e5e0d8]"
      }`}
      data-testid={`top-rated-card-${u.id}`}
    >
      {/* Big serif rank plate */}
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center font-serif-display text-3xl ${
            isChampion ? "bg-[#c39d2b] text-[#2a2010]" : "bg-white/95 text-[#722f37]"
          } shadow`}
          data-testid={`top-rated-rank-${u.id}`}
        >
          {rank}
        </div>
        {isChampion && (
          <div className="glass px-3 py-1.5 rounded-full text-xs font-bold text-[#722f37] flex items-center gap-1">
            <Crown className="w-3.5 h-3.5 text-[#c39d2b]" /> #1
          </div>
        )}
      </div>

      {/* Rating chip */}
      <div className="absolute top-4 right-4 z-10 glass rounded-full px-3 py-1.5 flex items-center gap-1 text-xs font-semibold text-[#722f37]" data-testid={`top-rated-rating-${u.id}`}>
        <StarIcon className="w-3.5 h-3.5 text-[#c39d2b] fill-[#c39d2b]" />
        {(u.overall_rating || 0).toFixed(1)}/10 · {u.rating_count}
      </div>

      {/* Compatibility chip (below rank) */}
      {typeof u.compatibility === "number" && (
        <div
          className="absolute top-20 left-4 z-10 rounded-full px-3 py-1 text-xs font-semibold bg-[#722f37] text-white shadow"
          title="Compatibility with your interests, city and age"
          data-testid={`top-rated-compat-${u.id}`}
        >
          {u.compatibility}% match
        </div>
      )}

      <div className="relative aspect-[3/4] bg-[#eae5d9] overflow-hidden">
        {photo?.data_url ? (
          <Link
            to={`/u/${u.id}`}
            className="block w-full h-full"
            data-testid={`top-rated-view-profile-${u.id}`}
            aria-label={`View ${u.display_name}'s profile`}
          >
            <img
              src={photo.data_url}
              alt={u.display_name}
              className="w-full h-full object-cover"
              data-testid={`top-rated-photo-${u.id}`}
            />
          </Link>
        ) : (
          <div className="w-full h-full" style={{ background: "linear-gradient(135deg,#c9b8a0,#a08a70)" }} />
        )}

        {/* Bottom gradient with identity */}
        <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif-display text-white text-3xl leading-tight" data-testid={`top-rated-name-${u.id}`}>
              {u.display_name}, <span className="opacity-80">{u.age}</span>
            </h3>
            {u.verified && <VerifiedBadge size={12} />}
          </div>
          {u.location && (
            <div className="mt-1 flex items-center gap-1 text-white/85 text-xs">
              <MapPin className="w-3 h-3" /> {u.location}
            </div>
          )}
          {u.voice_intro_url && (
            <div className="mt-2">
              <VoiceIntroPlayer
                url={u.voice_intro_url}
                durationMs={u.voice_intro_duration_ms}
                testId={`top-rated-voice-${u.id}`}
              />
            </div>
          )}
        </div>
      </div>

      <div className="p-5 flex flex-col gap-3 flex-1">
        {u.bio && <p className="text-sm text-[#5c5c5c] line-clamp-3">{u.bio}</p>}
        {u.interests?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {u.interests.slice(0, 3).map((tag) => (
              <span key={tag} className="px-2 py-0.5 rounded-full bg-[#faf9f6] border border-[#e5e0d8] text-[11px] text-[#5c5c5c]">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Inline rating strip — one tap to rate from the grid. */}
        {!isSelf && photo?.id && (
          <div className="rounded-xl bg-[#faf9f6] border border-[#e5e0d8] px-3 py-2.5" data-testid={`top-rated-rate-strip-${u.id}`}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#722f37]">Rate this photo</span>
              {photo.my_rating != null && (
                <span className="text-[10px] text-[#c39d2b] font-semibold" data-testid={`top-rated-my-rating-${u.id}`}>
                  You: {photo.my_rating}/10
                </span>
              )}
            </div>
            <NumericRating
              value={photo.my_rating || 0}
              onChange={handleRate}
              readOnly={rating != null}
              testId={`top-rated-rate-${u.id}`}
              compact
            />
          </div>
        )}

        <div className="mt-auto pt-3">
          {unlocked ? (
            <Link
              to={`/chat/${item.match_id}`}
              className="w-full flex items-center justify-center gap-2 rounded-full bg-[#722f37] text-white py-2.5 text-sm font-medium hover:bg-[#8c3a45] transition-colors"
              data-testid={`top-rated-chat-${u.id}`}
            >
              <Send className="w-4 h-4" /> Send message
            </Link>
          ) : subscribed ? (
            <button
              onClick={onContactCredit}
              disabled={busy}
              className="btn-primary w-full flex items-center justify-center gap-2 !py-2.5 text-sm"
              data-testid={`top-rated-contact-sub-${u.id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Contact (included)
            </button>
          ) : credits > 0 ? (
            <>
              <button
                onClick={onContactCredit}
                disabled={busy}
                className="btn-primary w-full flex items-center justify-center gap-2 !py-2.5 text-sm"
                data-testid={`top-rated-contact-credit-${u.id}`}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
                Contact (use 1 credit · {credits} left)
              </button>
              <button
                onClick={onContactPaid}
                disabled={busy}
                className="text-xs text-[#5c5c5c] underline hover:text-[#722f37] mt-2 mx-auto block"
                data-testid={`top-rated-contact-paid-${u.id}`}
              >
                or pay $10 once
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onContactPaid}
                disabled={busy}
                className={`w-full flex items-center justify-center gap-2 !py-2.5 text-sm font-semibold rounded-full transition-all ${
                  isChampion ? "btn-gold" : "btn-primary"
                }`}
                data-testid={`top-rated-contact-paid-${u.id}`}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                Contact for $10
              </button>
              <Link to="/pricing" className="text-xs text-[#5c5c5c] underline hover:text-[#722f37] mt-2 mx-auto block text-center">
                or save with a 3-pack / 10-pack
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
